import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { ArchiveRestore } from 'lucide-react';
import { useState } from 'react';
import { Button, Dialog } from '~/components/primitives';
import { Select } from '~/components/primitives/Select';
import { fetchProjectApprovers } from '~/lib/api/projects';
import { useAuthStore } from '~/store/auth';
import { ApproverEmptyNote, hasNoApproverCandidates } from './ApproverEmptyNote';
import styles from './CancelProjectDialog.module.css';

/**
 * F9 (project-actions-parity §3.1) — возврат проекта из архива.
 *
 * До B10/B12 возврата не существовало в принципе: `DELETE /:id` удалял строки
 * членства и связи, и восстанавливать было физически не из чего. Архив был
 * односторонней дверью, а архивный проект — достижим только прямой ссылкой,
 * которую надо откуда-то знать.
 *
 * Проекты, архивированные ДО появления перечня в журнале, возвращаются частично:
 * сам проект вернётся, его задачи останутся архивными. Диалог обязан сказать это
 * ДО подтверждения, а не после (SOLUTION §8.2-О1) — отсюда `partial`.
 */
/**
 * F1 (tier2-approval-requests §5) — подписи в теле больше нет: при Tier 2 уходит
 * ЗАЯВКА названному адресату, и объект не меняется до его решения.
 */
type RestorePayload = {
  reason: string;
  /** Адресат заявки: действие Tier 2 без него не отправляется. */
  addressee_user_id?: string;
};

const MIN_REASON = 10;

export function RestoreProjectDialog(props: {
  open: boolean;
  projectId: string;
  projectName: string;
  /**
   * Возврат неполный: у проекта нет перечня задач в журнале архивации.
   * Определяется вызывающим (он знает версию audit-записи) либо остаётся
   * `undefined` — тогда диалог говорит общую правду про задачи.
   */
  partial?: boolean;
  onConfirm: (payload: RestorePayload) => void;
  onCancel: () => void;
}): JSX.Element {
  const currentUserId = useAuthStore((s) => s.user?.user_id ?? s.user?.id ?? '');
  const [reason, setReason] = useState('');
  const [approverUserId, setApproverUserId] = useState('');
  const reasonOk = reason.trim().length >= MIN_REASON;
  const approverOk = !!approverUserId && approverUserId !== currentUserId;
  const valid = reasonOk && approverOk;

  const approversQuery = useQuery({
    queryKey: ['project-approvers', 'project.restore'],
    queryFn: () => fetchProjectApprovers({ action: 'project.restore' }),
    enabled: props.open,
    staleTime: 60_000,
  });
  const approverOptions = (approversQuery.data?.items ?? [])
    .filter((a) => a.id !== currentUserId)
    .map((a) => ({ value: a.id, label: a.full_name ?? a.email }));
  // F8 — тот же источник правды, что у остальных диалогов с подписью.
  const noApproverAvailable = hasNoApproverCandidates(approversQuery.data);

  const blockReason = noApproverAvailable
    ? null
    : !approverOk
      ? m.approval_request_block_addressee()
      : !reasonOk
        ? m.project_cancel_block_reason()
        : null;

  return (
    <Dialog open={props.open} onClose={props.onCancel} title={m.project_restore_title()}>
      <div className={styles.body}>
        <div className={styles.subject}>
          <span className={styles.subjectName}>{props.projectName}</span>
        </div>

        {/* Возврат из архива — не деструктивное действие, поэтому блок нейтральный
            (§1.3: семантический цвет только при отклонении). Предупреждающей может
            быть одна строка — про неполноту возврата. */}
        <div className={`${styles.impact} ${styles.impactInfo}`}>
          <span className={styles.impactTitle}>
            <ArchiveRestore size={15} aria-hidden="true" />
            {m.project_cancel_impact_title()}
          </span>
          <ul className={styles.impactList}>
            {props.partial ? (
              <li className={styles.impactWarnLine}>{m.project_restore_impact_partial()}</li>
            ) : (
              <li>{m.project_restore_impact_tasks()}</li>
            )}
            <li>{m.project_uncancel_impact_immediate()}</li>
          </ul>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {m.project_restore_reason_label()}
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          </span>
          <textarea
            className={styles.textarea}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
            aria-describedby="restore-reason-hint"
          />
          <span className={styles.hint} id="restore-reason-hint">
            {m.project_cancel_reason_hint({ min: MIN_REASON })}
          </span>
        </label>

        <Select
          label={m.approval_request_addressee_label()}
          value={approverUserId}
          onChange={(e) => setApproverUserId(e.target.value)}
          options={approverOptions}
          placeholder={m.approval_request_addressee_placeholder()}
          required
          disabled={noApproverAvailable}
        />
        <span className={styles.hint}>
          {noApproverAvailable ? (
            <ApproverEmptyNote data={approversQuery.data} />
          ) : (
            m.approval_request_addressee_hint()
          )}
        </span>

        <div className={styles.blockReasonRow} role="status" aria-live="polite">
          {blockReason !== null && <span className={styles.blockReason}>{blockReason}</span>}
        </div>

        <div className={styles.footer}>
          <Button variant="ghost" onClick={props.onCancel}>
            {m.project_restore_keep()}
          </Button>
          <Button
            disabled={!valid}
            onClick={() => {
              props.onConfirm({ reason, addressee_user_id: approverUserId });
            }}
          >
            {m.approval_request_cta()}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
